import BoundingBox from './bounding_box';
import Line from './line';
import Point from './point';

class LineSegment {
	x1 = 0;

	y1 = 0;

	x2 = 0;

	y2 = 0;

	length = 0;

	line = null;

	boundingBox = null;

	get isVertical() {
		return this.line.slope === undefined;
	}

	get isHorizontal() {
		return this.line.slope === 0;
	}

	constructor(x1, y1, x2, y2) {
		this.x1 = x1;
		this.y1 = y1;
		this.x2 = x2;
		this.y2 = y2;

		this.length = Math.hypot(x2 - x1, y2 - y1);

		this.line = Line.fromCoords(x1, y1, x2, y2);

		this.boundingBox = BoundingBox.fromPoints([new Point(x1, y1), new Point(x2, y2)]);
	}

	static fromPoints(point1, point2) {
		return new LineSegment(point1.x, point1.y, point2.x, point2.y);
	}

	equalsTo(line) {
		return (
			(this.x1 === line.x1 && this.y1 === line.y1 && this.x2 === line.x2 && this.y2 === line.y2) ||
			(this.x1 === line.x2 && this.y1 === line.y2 && this.x2 === line.x1 && this.y2 === line.y1)
		);
	}

	getScalarProductWith(lineSegment) {
		return (
			(this.x2 - this.x1) * (lineSegment.x2 - lineSegment.x1) + (this.y2 - this.y1) * (lineSegment.y2 - lineSegment.y1)
		);
	}

	getScalarAngleBetween(lineSegment) {
		const scalarProduct = this.getScalarProductWith(lineSegment);

		return (Math.acos(scalarProduct / (this.length * lineSegment.length)) * 180) / Math.PI;
	}

	getVectorProductWith(lineSegment) {
		const z =
			(this.x2 - this.x1) * (lineSegment.y2 - lineSegment.y1) - (this.y2 - this.y1) * (lineSegment.x2 - lineSegment.x1);

		return {
			x: 0,
			y: 0,
			z: Number(z.toFixed(5)),
		};
	}

	getCounterClockwiseAngleBetween(lineSegment) {
		const scalarAngle = this.getScalarAngleBetween(lineSegment);
		const { z } = this.getVectorProductWith(lineSegment);

		if (z > 0) {
			return scalarAngle;
		}

		if (z < 0) {
			return 360 - scalarAngle;
		}

		return 180;
	}

	isParallelWith(line) {
		if (line instanceof LineSegment) {
			return this.line.isParallelWith(line.line);
		}

		if (line instanceof Line) {
			return this.line.isParallelWith(line);
		}

		return false;
	}

	isPerpendicularWith(line) {
		if (line instanceof LineSegment) {
			return this.line.isPerpendicularWith(line.line);
		}

		if (line instanceof Line) {
			return this.line.isPerpendicularWith(line);
		}

		return false;
	}

	isCollinearWith(line) {
		if (line instanceof LineSegment) {
			return this.line.isCollinearWith(line.line);
		}

		if (line instanceof Line) {
			return this.line.isCollinearWith(line);
		}

		return false;
	}

	intersectsWith(line, checkBoundingBox = true) {
		if (line instanceof LineSegment) {
			const { x1 } = this;
			const { y1 } = this;
			const { x2 } = this;
			const { y2 } = this;
			const x3 = line.x1;
			const y3 = line.y1;
			const x4 = line.x2;
			const y4 = line.y2;

			const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4));
			const u = ((x1 - x3) * (y1 - y2) - (y1 - y3) * (x1 - x2)) / ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4));

			let intersection = null;

			if (t >= 0 && t <= 1) {
				intersection = new Point(x1 + t * (x2 - x1), y1 + t * (y2 - y1));
			}

			if (u >= 0 && u <= 1) {
				intersection = new Point(x3 + u * (x4 - x3), y3 + u * (y4 - y3));
			}

			if (intersection) {
				if (
					checkBoundingBox &&
					(!this.boundingBox.containsPoint(intersection, true) || !line.boundingBox.containsPoint(intersection, true))
				) {
					return false;
				}

				return intersection;
			}

			if (this.boundingBox.overlapsWith(line.boundingBox, true) && this.isCollinearWith(line)) {
				const points = [
					new Point(
						Math.max(Math.min(this.x1, this.x2), Math.min(line.x1, line.x2)),
						Math.max(Math.min(this.y1, this.y2), Math.min(line.y1, line.y2))
					),
					new Point(
						Math.min(Math.max(this.x1, this.x2), Math.max(line.x1, line.x2)),
						Math.min(Math.max(this.y1, this.y2), Math.max(line.y1, line.y2))
					),
				];

				if (points[0].x === points[1].x && points[0].y === points[1].y) {
					return false;
				}

				return points;
			}
		}

		if (line instanceof Line) {
			const intersection = this.line.intersectsWith(line);

			if (intersection) {
				if (checkBoundingBox && !this.boundingBox.containsPoint(intersection)) {
					return false;
				}

				return intersection;
			}
		}

		return false;
	}

	getPerpendicularLine(point) {
		return this.line.getPerpendicularLine(point);
	}

	containsPoint(point) {
		return this.boundingBox.containsPoint(point) && this.line.containsPoint(point);
	}

	getPointFromX(x) {
		if (x < this.boundingBox.left && x > this.boundingBox.right) {
			return null;
		}

		return this.line.getPointFromX(x);
	}

	getPointFromY(y) {
		if (y < this.boundingBox.top && y > this.boundingBox.bottom) {
			return null;
		}

		return this.line.getPointFromY(y);
	}
}

export default LineSegment;
